import React, { useState, useEffect } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import CustomDropdown from './CustomDropdown';

interface RegistrationCode {
  id: number;
  code: string;
  description: string | null;
  created_by: number;
  created_at: string;
  used_at: string | null;
  used_by: number | null;
  expires_at: string | null;
  is_active: boolean;
  admin_username?: string;
  created_by_username?: string;
  used_by_username: string | null;
  grants_can_organize?: boolean;
  grants_can_judge?: boolean;
  max_uses?: number;
  use_count?: number;
  remaining_uses?: number;
  status?: string;
}

export const RegistrationCodeManagement: React.FC = () => {
  const { showToast } = useToast();
  const [codes, setCodes] = useState<RegistrationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'used' | 'expired'>('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<RegistrationCode | null>(null);
  
  // Form states
  const [newCodeDescription, setNewCodeDescription] = useState('');
  const [newCodeExpiration, setNewCodeExpiration] = useState('');
  const [newCodeGrantOrganize, setNewCodeGrantOrganize] = useState(false);
  const [newCodeGrantJudge, setNewCodeGrantJudge] = useState(false);
  const [newCodeMaxUses, setNewCodeMaxUses] = useState(1);

  const loadCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: {
        page: number;
        limit: number;
        search?: string;
        status_filter?: 'active' | 'used' | 'expired';
        sort_by: string;
        sort_order: 'asc' | 'desc';
      } = {
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
        status_filter: statusFilter !== 'all' ? (statusFilter as 'active' | 'used' | 'expired') : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      const data = await apiService.getRegistrationCodes(params);
      setCodes(data.data || []);
      setPagination(data.pagination);
      setStatistics(data.statistics);
    } catch (err: any) {
      console.error('Failed to load codes:', err);
      setError(err.message || 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when list filters change
  }, [currentPage, searchTerm, statusFilter, sortBy, sortOrder]);

  const handleCreateCode = async () => {
    try {
      const codeData: any = {
        description: newCodeDescription || undefined,
        grants_can_organize: newCodeGrantOrganize,
        grants_can_judge: newCodeGrantJudge,
        max_uses: Math.max(1, Number(newCodeMaxUses) || 1),
      };
      
      if (newCodeExpiration) {
        const days = Number(newCodeExpiration);
        if (!Number.isNaN(days) && days > 0) {
          codeData.expires_days = days;
        } else {
          codeData.expires_at = newCodeExpiration;
        }
      }

      await apiService.createRegistrationCode(codeData);
      await loadCodes(); // Reload codes
      setShowCreateModal(false);
      setNewCodeDescription('');
      setNewCodeExpiration('');
      setNewCodeGrantOrganize(false);
      setNewCodeGrantJudge(false);
      setNewCodeMaxUses(1);
    } catch (err: any) {
      console.error('Failed to create code:', err);
      showToast(err.message || 'Unable to create code.', 'error');
    }
  };

  const handleUpdateCode = async (codeId: number, updates: any) => {
    try {
      await apiService.updateRegistrationCode(codeId, updates);
      await loadCodes(); // Reload codes
      setShowEditModal(false);
      setSelectedCode(null);
    } catch (err: any) {
      console.error('Failed to update code:', err);
      showToast(err.message || 'Unable to update code.', 'error');
    }
  };

  const handleDeleteCode = async (codeId: number) => {
    try {
      await apiService.deleteRegistrationCode(codeId);
      await loadCodes(); // Reload codes
      setShowDeleteModal(false);
      setSelectedCode(null);
    } catch (err: any) {
      console.error('Failed to delete code:', err);
      showToast(err.message || 'Unable to delete code.', 'error');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showToast('Copied to clipboard.', 'success');
        return;
      }
      
      // Fallback for older browsers or non-HTTPS contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showToast('Copied to clipboard.', 'success');
        } else {
          throw new Error('Copy command failed');
        }
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Show the text in a prompt as last resort
      prompt('Copy this text:', text);
    }
  };

  const generateShareableLink = (code: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?code=${code}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (code: RegistrationCode) => {
    const maxUses = code.max_uses ?? 1;
    const useCount = code.use_count ?? 0;
    if (!code.is_active) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#1a1a1a] text-white">Inactive</span>;
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white">Expired</span>;
    }
    if (useCount >= maxUses) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">Exhausted</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">Active</span>;
  };

  if (loading && (!codes || codes.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading codes...</p>
        </div>
      </div>
    );
  }

  if (error && (!codes || codes.length === 0)) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-red-400 font-medium">Failed to load codes</h3>
            <p className="text-gray-300 text-sm mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadCodes}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader eyebrow="Admin" title="Invite codes" description="Create and manage registration invite codes." />
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Generate Code
          </button>
          <button
            onClick={loadCodes}
            className="bg-[#1a1a1a] hover:bg-white/[0.06] text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#141414] rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total Codes</h3>
            <p className="text-2xl font-bold text-white">{statistics.total_codes}</p>
          </div>
          <div className="bg-[#141414] rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Active</h3>
            <p className="text-2xl font-bold text-blue-400">{statistics.active_codes}</p>
          </div>
          <div className="bg-[#141414] rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Used</h3>
            <p className="text-2xl font-bold text-green-400">{statistics.used_codes}</p>
          </div>
          <div className="bg-[#141414] rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Expired</h3>
            <p className="text-2xl font-bold text-red-400">{statistics.expired_codes}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#141414] rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search codes..."
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'used' | 'expired')}
              className="input"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input"
            >
              <option value="created_at">Created Date</option>
              <option value="code">Code</option>
              <option value="expires_at">Expiration</option>
              <option value="used_at">Used Date</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">Order</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="input"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#161616]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Grants</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.08]">
              {(codes || []).map((code) => (
                <tr key={code.id} className="hover:bg-[#161616]/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-white font-mono">{code.code}</div>
                      {code.description && (
                        <div className="text-sm text-gray-400">{code.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(code)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <div className="flex flex-wrap gap-1">
                      {code.grants_can_organize ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-600 text-white">Organizer</span>
                      ) : null}
                      {code.grants_can_judge ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-amber-600 text-white">Judge</span>
                      ) : null}
                      {!code.grants_can_organize && !code.grants_can_judge ? (
                        <span className="text-gray-500">Member</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <div>By: {code.created_by_username || code.admin_username || '-'}</div>
                    <div>{formatDate(code.created_at)}</div>
                    {code.expires_at && (
                      <div className="text-xs">Expires: {formatDate(code.expires_at)}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <div>
                      {(code.use_count ?? 0)} / {(code.max_uses ?? 1)} uses
                    </div>
                    {code.used_by_username ? (
                      <div className="text-xs text-gray-500">Last: {code.used_by_username}</div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => copyToClipboard(code.code)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        title="Copy code"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => copyToClipboard(generateShareableLink(code.code))}
                        className="text-green-400 hover:text-green-300 transition-colors"
                        title="Copy shareable link"
                      >
                        Link
                      </button>
                      {(code.use_count ?? 0) < (code.max_uses ?? 1) && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedCode(code);
                              setShowEditModal(true);
                            }}
                            className="text-orange-400 hover:text-orange-300 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCode(code);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} codes
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="btn-secondary btn-sm pointer-events-none">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Code Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setNewCodeDescription('');
          setNewCodeExpiration('');
          setNewCodeGrantOrganize(false);
          setNewCodeGrantJudge(false);
          setNewCodeMaxUses(1);
        }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card mb-4">Generate New Invite Code</h3>
            <div className="space-y-4">
              <div>
                <label className="input-label">Description (optional)</label>
                <input
                  type="text"
                  value={newCodeDescription}
                  onChange={(e) => setNewCodeDescription(e.target.value)}
                  placeholder="e.g., Organizer invite for Peak Meet"
                  className="input"
                />
              </div>
              <div>
                <label className="input-label">Expiration Date (optional)</label>
                <input
                  type="datetime-local"
                  value={newCodeExpiration}
                  onChange={(e) => setNewCodeExpiration(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="input-label">Max uses</label>
                <input
                  type="number"
                  min={1}
                  value={newCodeMaxUses}
                  onChange={(e) => setNewCodeMaxUses(Math.max(1, Number(e.target.value) || 1))}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">1 = one-time invite. Higher values allow multi-use codes.</p>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-3 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={newCodeGrantOrganize}
                    onChange={(e) => setNewCodeGrantOrganize(e.target.checked)}
                    className="checkbox-control"
                  />
                  Grant organizer access (events & clubs)
                </label>
                <label className="flex items-center gap-3 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={newCodeGrantJudge}
                    onChange={(e) => setNewCodeGrantJudge(e.target.checked)}
                    className="checkbox-control"
                  />
                  Grant judge access
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCodeDescription('');
                  setNewCodeExpiration('');
                  setNewCodeGrantOrganize(false);
                  setNewCodeGrantJudge(false);
                  setNewCodeMaxUses(1);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCode}
                className="btn-primary"
              >
                Generate Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Code Modal */}
      {showEditModal && selectedCode && (
        <div className="modal-overlay" onClick={() => { setShowEditModal(false); setSelectedCode(null); }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card mb-4">Edit Invite Code</h3>
            <div className="space-y-4">
              <div>
                <label className="input-label">Code</label>
                <input
                  type="text"
                  value={selectedCode.code}
                  disabled
                  className="input opacity-60 font-mono"
                />
              </div>
              <div>
                <label className="input-label">Description</label>
                <input
                  type="text"
                  defaultValue={selectedCode.description || ''}
                  placeholder="e.g., For beta testers"
                  className="input"
                  onChange={(e) => setSelectedCode({...selectedCode, description: e.target.value})}
                />
              </div>
              <div>
                <label className="input-label">Status</label>
                <CustomDropdown
                  value={selectedCode.is_active ? 'active' : 'inactive'}
                  onChange={(value) => setSelectedCode({...selectedCode, is_active: value === 'active'})}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                  placeholder="Select status"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCode(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateCode(selectedCode.id, {
                  description: selectedCode.description,
                  is_active: selectedCode.is_active
                })}
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Code Modal */}
      {showDeleteModal && selectedCode && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setSelectedCode(null); }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card text-red-400 mb-4">Delete Invite Code</h3>
            <p className="text-muted mb-6 text-sm">
              Are you sure you want to permanently delete the code <strong className="font-mono text-white">{selectedCode.code}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCode(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCode(selectedCode.id)}
                className="btn-danger"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 